/**
 * Browser Notification Service
 * Handles permission requests and sending notifications for the SeeD Laboratory Management System
 */

class NotificationService {
    constructor() {
        // Check if we're in a browser environment and if Notification API is available
        this.isSupported = (
            typeof window !== 'undefined' &&
            'Notification' in window &&
            typeof Notification !== 'undefined'
        );

        // Only access Notification.permission if the API is available
        this.permission = this.isSupported ? Notification.permission : 'denied';

        // Development mode fallback
        this.isDevelopment = process.env.NODE_ENV === 'development';

        // Log initialization status in development
        if (this.isDevelopment) {
            console.log('NotificationService initialized:', {
                isSupported: this.isSupported,
                permission: this.permission,
                environment: process.env.NODE_ENV,
                userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
            });
        }
    }    /**
     * Check if browser supports notifications
     */
    isNotificationSupported() {
        return this.isSupported;
    }

    /**
     * Get current notification permission status
     */
    getPermissionStatus() {
        if (!this.isSupported) {
            return 'denied';
        }
        return Notification.permission;
    }

    /**
     * Request notification permission from user
     */
    async requestPermission() {
        if (!this.isSupported) {
            throw new Error('Browser notifications are not supported');
        }

        const currentPermission = this.getPermissionStatus();

        if (currentPermission === 'granted') {
            return 'granted';
        }

        if (currentPermission === 'denied') {
            return 'denied';
        }

        try {
            const permission = await Notification.requestPermission();
            this.permission = permission;
            return permission;
        } catch (error) {
            console.error('Failed to request notification permission:', error);
            throw error;
        }
    }

    /**
     * Send a notification
     */
    sendNotification(title, options = {}) {
        if (!this.isSupported) {
            if (this.isDevelopment) {
                console.log('🔔 [DEV] Notification would be sent:', { title, options });
            } else {
                console.warn('Notifications not supported');
            }
            return null;
        }

        const currentPermission = this.getPermissionStatus();
        if (currentPermission !== 'granted') {
            if (this.isDevelopment) {
                console.log('🔔 [DEV] Notification permission not granted:', currentPermission, '| Would send:', title);
            } else {
                console.warn('Notification permission not granted, current status:', currentPermission);
            }
            return null;
        }

        const defaultOptions = {
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            timestamp: Date.now(),
            requireInteraction: false,
            ...options
        };

        try {
            const notification = new Notification(title, defaultOptions);

            // Auto-close notification after 8 seconds if not requiring interaction
            if (!defaultOptions.requireInteraction) {
                setTimeout(() => {
                    notification.close();
                }, 8000);
            }

            if (this.isDevelopment) {
                console.log('🔔 [DEV] Notification sent successfully:', title);
            }

            return notification;
        } catch (error) {
            console.error('Failed to send notification:', error);
            if (this.isDevelopment) {
                console.log('🔔 [DEV] Notification fallback - would show:', { title, options });
            }
            return null;
        }
    }

    /**
     * Send maintenance request notifications
     */
    sendMaintenanceNotification(type, data) {
        const notifications = {
            'request_assigned': {
                title: '🔧 Maintenance Request Assigned',
                options: {
                    body: `Request #${data.tokenNumber} has been assigned to ${data.workerName}`,
                    icon: '/favicon.ico',
                    tag: `maintenance-${data.id}`,
                    requireInteraction: true,
                    actions: [
                        { action: 'view', title: 'View Request' },
                        { action: 'dismiss', title: 'Dismiss' }
                    ]
                }
            },
            'request_status_updated': {
                title: '📋 Request Status Updated',
                options: {
                    body: `Request #${data.tokenNumber} status changed to ${data.status}`,
                    icon: '/favicon.ico',
                    tag: `maintenance-${data.id}`,
                    requireInteraction: false
                }
            },
            'new_request_created': {
                title: '🆕 New Maintenance Request',
                options: {
                    body: `New ${data.category} request from ${data.requesterName}`,
                    icon: '/favicon.ico',
                    tag: `new-request-${data.id}`,
                    requireInteraction: true,
                    actions: [
                        { action: 'assign', title: 'Assign Worker' },
                        { action: 'view', title: 'View Details' }
                    ]
                }
            },
            'request_completed': {
                title: '✅ Request Completed',
                options: {
                    body: `Request #${data.tokenNumber} has been resolved`,
                    icon: '/favicon.ico',
                    tag: `maintenance-${data.id}`,
                    requireInteraction: false
                }
            },
            'worker_task_assigned': {
                title: '👷 New Task Assigned',
                options: {
                    body: `You have been assigned to request #${data.tokenNumber}`,
                    icon: '/favicon.ico',
                    tag: `task-${data.id}`,
                    requireInteraction: true,
                    actions: [
                        { action: 'accept', title: 'Accept Task' },
                        { action: 'view', title: 'View Details' }
                    ]
                }
            },
            'urgent_request': {
                title: '🚨 Urgent Maintenance Request',
                options: {
                    body: `URGENT: ${data.category} request requires immediate attention`,
                    icon: '/favicon.ico',
                    tag: `urgent-${data.id}`,
                    requireInteraction: true,
                    vibrate: [200, 100, 200],
                    actions: [
                        { action: 'assign', title: 'Assign Now' },
                        { action: 'escalate', title: 'Escalate' }
                    ]
                }
            }
        };

        const notificationConfig = notifications[type];
        if (!notificationConfig) {
            console.warn(`Unknown notification type: ${type}`);
            return null;
        }

        return this.sendNotification(notificationConfig.title, notificationConfig.options);
    }

    /**
     * Send role-based notifications
     */
    sendRoleBasedNotification(userRole, type, data) {
        // Role-based notification rules
        const roleNotifications = {
            'manager': ['new_request_created', 'urgent_request', 'request_completed'],
            'admin': ['new_request_created', 'request_status_updated', 'urgent_request'],
            'faculty': ['request_assigned', 'request_status_updated'],
            'worker': ['worker_task_assigned'],
            'scholar': ['request_status_updated'],
            'student': ['request_status_updated']
        };

        const allowedTypes = roleNotifications[userRole] || [];

        if (allowedTypes.includes(type)) {
            return this.sendMaintenanceNotification(type, data);
        }

        return null;
    }

    /**
     * Handle notification clicks
     */
    handleNotificationClick(notification, action, callback) {
        if (callback && typeof callback === 'function') {
            callback(action, notification);
        }
        notification.close();
    }

    /**
     * Clear all notifications with specific tag
     */
    clearNotificationsByTag(tag) {
        // Note: This is limited by browser API - we can only close notifications we created
        console.log(`Attempting to clear notifications with tag: ${tag}`);
    }

    /**
     * Check and request permission with user-friendly dialog
     */
    async checkAndRequestPermission() {
        if (!this.isSupported) {
            return {
                success: false,
                message: 'Your browser does not support notifications'
            };
        }

        const currentPermission = this.getPermissionStatus();

        if (currentPermission === 'granted') {
            return {
                success: true,
                message: 'Notification permission already granted'
            };
        }

        if (currentPermission === 'denied') {
            return {
                success: false,
                message: 'Notification permission was denied. Please enable in browser settings.'
            };
        }

        try {
            const permission = await this.requestPermission();

            if (permission === 'granted') {
                return {
                    success: true,
                    message: 'Notification permission granted successfully'
                };
            } else {
                return {
                    success: false,
                    message: 'Notification permission was not granted'
                };
            }
        } catch (error) {
            return {
                success: false,
                message: 'Failed to request notification permission'
            };
        }
    }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
